/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Toast notification system with React bridge integration
 * toasts.client.ts - Standalone notification system with fallback DOM implementation
 */

export type ToastType = "info" | "success" | "warning" | "error";

export interface ToastConfig {
  defaultDuration: number;
  animationDuration: number;
  maxToasts: number;
  containerSelector: string;
}

export interface ReactUIBridge {
  addToast: (message: string, type: ToastType, duration: number) => void;
}

const DEFAULT_TOAST_CONFIG: ToastConfig = {
  defaultDuration: 3000,
  animationDuration: 300,
  maxToasts: 5,
  containerSelector: "toast-container",
};

let toastConfig = DEFAULT_TOAST_CONFIG;
let toastContainer: HTMLElement | null = null;
let activeToastCount = 0;

/**
 * Configure toast notification parameters
 * @param config - Toast configuration options
 */
export function configureToasts(config: Partial<ToastConfig>): void {
  toastConfig = { ...DEFAULT_TOAST_CONFIG, ...config };
  toastContainer = null;
}

function getToastContainer(): HTMLElement | null {
  if (!toastContainer) {
    toastContainer = document.getElementById(toastConfig.containerSelector);
  }
  return toastContainer;
}

export function isReactBridgeAvailable(): boolean {
  return !!(window as any).uiBridge?.addToast;
}

/**
 * Display toast notification with React bridge or DOM fallback
 * Automatically uses React bridge if available, falls back to vanilla DOM
 * @param message - Toast message to display
 * @param type - Toast type (info, success, warning, error)
 * @param duration - Duration to show toast in milliseconds
 */
export function showToast(
  message: string,
  type: ToastType = "info",
  duration: number = toastConfig.defaultDuration,
): void {
  if (isReactBridgeAvailable()) {
    try {
      const bridge = (window as any).uiBridge as ReactUIBridge;
      bridge.addToast(message, type, duration);
      return;
    } catch (error) {
      console.warn("React bridge failed, falling back to DOM:", error);
    }
  }

  showDOMToast(message, type, duration);
}

export function showDOMToast(
  message: string,
  type: ToastType = "info",
  duration: number = toastConfig.defaultDuration,
): void {
  const container = getToastContainer();
  if (!container) {
    console.warn(
      `Container '${toastConfig.containerSelector}' not found, falling back to console`,
    );
    console.log(`${type.toUpperCase()} toast: ${message}`);
    return;
  }

  if (activeToastCount >= toastConfig.maxToasts) {
    console.warn(
      `Maximum toast limit (${toastConfig.maxToasts}) reached, ignoring new toast`,
    );
    return;
  }

  const toast = document.createElement("div");
  toast.className = `toast ${type}`;
  toast.textContent = message;

  container.appendChild(toast);
  activeToastCount++;

  if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(() => {
      toast.classList.add("show");
    });
  } else {
    // Fallback if requestAnimationFrame is not available (testing environment)
    toast.classList.add("show");
  }

  setTimeout(() => {
    hideToast(toast);
  }, duration);
}

export function hideToast(toast: HTMLElement): void {
  toast.classList.remove("show");

  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
      activeToastCount = Math.max(0, activeToastCount - 1);
    }
  }, toastConfig.animationDuration);
}

/**
 * Clear all active toasts immediately
 * Useful for cleanup or when changing UI state
 */
export function clearAllToasts(): void {
  const container = getToastContainer();
  if (container) {
    const toasts = container.querySelectorAll(".toast");
    toasts.forEach((toast) => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    });
  }

  activeToastCount = 0;
}

export function getToastStats(): {
  activeToasts: number;
  maxToasts: number;
  hasReactBridge: boolean;
  hasContainer: boolean;
  config: ToastConfig;
} {
  return {
    activeToasts: activeToastCount,
    maxToasts: toastConfig.maxToasts,
    hasReactBridge: isReactBridgeAvailable(),
    hasContainer: getToastContainer() !== null,
    config: { ...toastConfig },
  };
}

/**
 * Reset toast container reference
 * Useful when DOM structure changes or for testing
 */
export function resetToastContainer(): void {
  toastContainer = null;
  activeToastCount = 0;
}

export const showInfoToast = (message: string, duration?: number) =>
  showToast(message, "info", duration);

export const showSuccessToast = (message: string, duration?: number) =>
  showToast(message, "success", duration);

export const showWarningToast = (message: string, duration?: number) =>
  showToast(message, "warning", duration);

export const showErrorToast = (message: string, duration?: number) =>
  showToast(message, "error", duration);
